import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Zap, AlertTriangle, Crown, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

interface UserSubscription {
  credits_remaining: number | null;
  credits_used: number;
  status: string;
  plans: {
    name: string;
    credits: number;
  } | null;
}

export const CreditsDisplay = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchSubscription();
    }
  }, [user]);

  const fetchSubscription = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select(`
          credits_remaining,
          credits_used,
          status,
          plans!inner(name, credits)
        `)
        .eq('user_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      if (error) {
        console.error('Error fetching subscription:', error);
        return;
      }

      setSubscription(data as any);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshSubscription = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Force refresh from database
      await fetchSubscription();
      toast.success('Subscription status refreshed');
    } catch (error) {
      console.error('Error refreshing subscription:', error);
      toast.error('Failed to refresh subscription status');
    }
  };

  // Update credits after processing
  useEffect(() => {
    const handleCreditsUpdate = (event: CustomEvent) => {
      if (subscription) {
        setSubscription({
          ...subscription,
          credits_remaining: event.detail.credits_remaining
        });
      }
    };

    window.addEventListener('creditsUpdated', handleCreditsUpdate as EventListener);
    return () => window.removeEventListener('creditsUpdated', handleCreditsUpdate as EventListener);
  }, [subscription]);

  if (!user || loading) return null;

  if (!subscription) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <AlertTriangle className="w-4 h-4 text-destructive" />
        <span>No active plan</span>
        <Link to="/pricing">
          <Button size="sm" variant="default">
            <Crown className="w-3 h-3 mr-1" />
            Upgrade
          </Button>
        </Link>
      </div>
    );
  }

  const isUnlimited = subscription.plans?.credits === -1 || subscription.credits_remaining === null;
  const isLowCredits = !isUnlimited && (subscription.credits_remaining as number) <= 5;
  const isOutOfCredits = !isUnlimited && (subscription.credits_remaining as number) <= 0;

  const getCreditsColor = () => {
    if (isUnlimited) return 'default';
    if (isOutOfCredits) return 'destructive';
    if (isLowCredits) return 'secondary';
    return 'default';
  };

  const handleUpgradeClick = () => {
    if (isOutOfCredits) {
      toast.error('No credits remaining! Please upgrade to continue using the humanizer.');
    } else if (isLowCredits) {
      toast.warning('Running low on credits! Consider upgrading to avoid interruptions.');
    }
  };

  return (
    <div className="flex items-center gap-2 text-sm">
      <div className="flex items-center gap-1">
        <Zap className="w-4 h-4 text-primary" />
        <Badge variant={getCreditsColor()}>
          {isUnlimited ? 'Unlimited' : `${subscription.credits_remaining} credits`}
        </Badge>
        <Button 
          size="sm" 
          variant="ghost" 
          onClick={refreshSubscription}
          disabled={loading}
          className="p-1 h-6 w-6"
          title="Refresh subscription status"
        >
          <RotateCcw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      
      {(!isUnlimited && (isLowCredits || isOutOfCredits)) && (
        <Link to="/pricing" onClick={handleUpgradeClick}>
          <Button size="sm" variant={isOutOfCredits ? "destructive" : "secondary"}>
            <Crown className="w-3 h-3 mr-1" />
            {isOutOfCredits ? 'Upgrade Now' : 'Get More'}
          </Button>
        </Link>
      )}
    </div>
  );
};